import {
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	getPaginationRowModel,
	getExpandedRowModel,
	flexRender,
} from '@tanstack/react-table';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import TextFilter from './filters/TextFilter';
import SelectFilter from './filters/SelectFilter';
import NumberFilter from './filters/NumberFilter';
import { __ } from '@wordpress/i18n';

const PAGE_SIZE = 50;

/**
 * Generic headless CRM table powered by TanStack Table v8.
 *
 * @param {Array}   columns   TanStack column definitions (from columns/*.js)
 * @param {Array}   data      Flat array of row objects
 * @param {boolean} isLoading Show loading state
 */
/**
 * @param         filters.columns
 * @param {Array} filters                   Optional array of { columnId, label, getTerms(row)→string[] }
 *                                          Each entry renders a SelectFilter dropdown in the toolbar.
 * @param         filters.data
 * @param         filters.isLoading
 * @param         filters.filters
 * @param         filters.numericFilters
 * @param         filters.searchPlaceholder
 * @param         filters.exportEvent
 * @param         filters.onExportRows
 * @param         filters.emailExportEvent
 * @param         filters.onEmailExportRows
 * @param         filters.renderSubRow
 * @param         filters.fitWidth
 * @param         filters.getRowClassName
 * @param         filters.initialSorting
 */
export default function CptTable( {
	columns,
	data,
	isLoading,
	filters = [],
	numericFilters = [],
	searchPlaceholder = __( 'Search…', 'anacrm' ),
	exportEvent,
	onExportRows,
	emailExportEvent,
	onEmailExportRows,
	renderSubRow,
	fitWidth = false,
	getRowClassName,
	initialSorting = [],
} ) {
	const [ globalFilter, setGlobalFilter ] = useState( () => {
		const params = new URLSearchParams( window.location.search );
		return params.get( 'q' ) ?? '';
	} );
	const [ columnFilters, setColumnFilters ] = useState( () => {
		const params = new URLSearchParams( window.location.search );
		const result = [];
		for ( const [ key, value ] of params ) {
			if ( key.startsWith( 'filter_' ) ) {
				const id = key.slice( 7 );
				const isNumeric = numericFilters.some(
					( f ) => f.columnId === id
				);
				result.push( {
					id,
					value: isNumeric ? Number( value ) : value,
				} );
			}
		}
		return result;
	} );
	const [ sorting, setSorting ] = useState( initialSorting );
	const [ expanded, setExpanded ] = useState( {} );
	const initialColumnVisibility = useMemo(
		() =>
			columns.reduce( ( acc, col ) => {
				if ( col.meta?.hidden ) {
					acc[ col.id ?? col.accessorKey ] = false;
				}
				return acc;
			}, {} ),
		[] // eslint-disable-line react-hooks/exhaustive-deps
	);
	const [ columnVisibility, setColumnVisibility ] = useState(
		initialColumnVisibility
	);
	const [ pagination, setPagination ] = useState( {
		pageIndex: 0,
		pageSize: PAGE_SIZE,
	} );

	// Derive unique sorted options for each select filter from the full dataset.
	const filterOptions = useMemo(
		() =>
			filters.map( ( f ) => {
				if ( f.type === 'text' ) {
					return { ...f, options: [] };
				}
				const terms = new Set();
				data.forEach( ( row ) =>
					f.getTerms( row ).forEach( ( t ) => terms.add( t ) )
				);
				if ( f.allowedValues ) {
					const allowed = new Set( f.allowedValues );
					[ ...terms ].forEach(
						( t ) => ! allowed.has( t ) && terms.delete( t )
					);
				}
				const dependencyValue = f.dependsOnColumnId
					? columnFilters.find(
							( cf ) => cf.id === f.dependsOnColumnId
					  )?.value
					: undefined;
				const disabled = Boolean(
					dependencyValue &&
						f.disabledWhenValues?.includes( dependencyValue )
				);
				if ( disabled ) {
					terms.clear();
				} else if (
					f.dependsOnColumnId &&
					f.relationMap &&
					dependencyValue
				) {
					[ ...terms ].forEach( ( t ) => {
						if ( f.relationMap[ t ] !== dependencyValue ) {
							terms.delete( t );
						}
					} );
				}
				return {
					...f,
					disabled,
					options: [ ...terms ]
						.sort()
						.map( ( t ) => ( { value: t, label: t } ) ),
				};
			} ),
		[ data, filters, columnFilters ]
	);

	// When a dependent filter's options shrink (e.g. territory narrows the
	// city list), drop any currently selected value that is no longer valid.
	useEffect( () => {
		setColumnFilters( ( prev ) => {
			let changed = false;
			const next = prev.filter( ( cf ) => {
				const f = filterOptions.find( ( fo ) => fo.columnId === cf.id );
				if ( ! f || ! f.dependsOnColumnId ) {
					return true;
				}
				const stillValid = f.options.some(
					( o ) => o.value === cf.value
				);
				if ( ! stillValid ) {
					changed = true;
				}
				return stillValid;
			} );
			return changed ? next : prev;
		} );
	}, [ filterOptions ] );

	const table = useReactTable( {
		data,
		columns,
		state: {
			globalFilter,
			columnFilters,
			sorting,
			pagination,
			columnVisibility,
			expanded,
		},
		onGlobalFilterChange: setGlobalFilter,
		onColumnFiltersChange: setColumnFilters,
		onSortingChange: setSorting,
		onPaginationChange: setPagination,
		onColumnVisibilityChange: setColumnVisibility,
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
	} );

	useEffect( () => {
		const params = new URLSearchParams( window.location.search );
		[ ...params.keys() ]
			.filter( ( k ) => k === 'q' || k.startsWith( 'filter_' ) )
			.forEach( ( k ) => params.delete( k ) );
		if ( globalFilter ) {
			params.set( 'q', globalFilter );
		}
		columnFilters.forEach( ( { id, value } ) => {
			if ( value !== undefined && value !== '' ) {
				params.set( `filter_${ id }`, value );
			}
		} );
		const qs = params.toString();
		history.replaceState(
			null,
			'',
			qs
				? `${ window.location.pathname }?${ qs }`
				: window.location.pathname
		);
	}, [ globalFilter, columnFilters ] );

	// Always-fresh ref so the event handler never captures a stale table instance.
	const tableRef = useRef( null );
	tableRef.current = table;

	useEffect( () => {
		if ( ! exportEvent || ! onExportRows ) {
			return;
		}
		const handler = () =>
			onExportRows(
				tableRef.current
					.getFilteredRowModel()
					.rows.map( ( r ) => r.original )
			);
		document.addEventListener( exportEvent, handler );
		return () => document.removeEventListener( exportEvent, handler );
	}, [ exportEvent, onExportRows ] );

	useEffect( () => {
		if ( ! emailExportEvent || ! onEmailExportRows ) {
			return;
		}
		const handler = () =>
			onEmailExportRows(
				tableRef.current
					.getFilteredRowModel()
					.rows.map( ( r ) => r.original )
			);
		document.addEventListener( emailExportEvent, handler );
		return () => document.removeEventListener( emailExportEvent, handler );
	}, [ emailExportEvent, onEmailExportRows ] );

	if ( isLoading ) {
		return (
			<div className="flex items-center justify-center py-16 text-gray">
				<span className="text-sm animate-pulse">Cargando…</span>
			</div>
		);
	}

	const { pageIndex, pageSize } = table.getState().pagination;
	const total = table.getFilteredRowModel().rows.length;
	const from = pageIndex * pageSize + 1;
	const to = Math.min( from + pageSize - 1, total );

	const hasActiveFilters = globalFilter || columnFilters.length > 0;

	function clearAllFilters() {
		setGlobalFilter( '' );
		setColumnFilters( [] );
	}

	return (
		<div className="flex flex-col gap-4">
			{ /* Toolbar */ }
			<div className="flex flex-wrap items-center gap-3 text-xs">
				<TextFilter
					value={ globalFilter }
					onChange={ setGlobalFilter }
					placeholder={ searchPlaceholder }
				/>

				{ filterOptions.map( ( f ) =>
					f.type === 'text' ? (
						<TextFilter
							key={ f.columnId }
							value={
								table
									.getColumn( f.columnId )
									?.getFilterValue() ?? ''
							}
							onChange={ ( v ) =>
								table
									.getColumn( f.columnId )
									?.setFilterValue( v || undefined )
							}
							placeholder={ f.label }
						/>
					) : (
						<SelectFilter
							key={ f.columnId }
							options={ f.options }
							value={
								table
									.getColumn( f.columnId )
									?.getFilterValue() ?? ''
							}
							onChange={ ( v ) =>
								table
									.getColumn( f.columnId )
									?.setFilterValue( v || undefined )
							}
							placeholder={ f.label }
							disabled={ f.disabled }
						/>
					)
				) }

				{ numericFilters.map( ( f ) => (
					<NumberFilter
						key={ f.columnId }
						value={
							table.getColumn( f.columnId )?.getFilterValue() ??
							''
						}
						onChange={ ( v ) =>
							table
								.getColumn( f.columnId )
								?.setFilterValue( v === '' ? undefined : v )
						}
						placeholder={ f.label }
					/>
				) ) }

				{ hasActiveFilters && (
					<button
						onClick={ clearAllFilters }
						className="px-3 py-2 border-2 border-black bg-white hover:bg-gray-light"
					>
						✕ Limpiar filtros
					</button>
				) }

				<span className="ml-auto text-gray">
					{ total } resultado{ total !== 1 ? 's' : '' }
				</span>
			</div>

			{ /* Table — outer div flips vertically so the horizontal scrollbar appears at the top */ }
			<div className="overflow-x-auto [transform:scaleY(-1)]">
				<div className="[transform:scaleY(-1)]">
					<table className="w-full border-collapse text-xs">
						<thead>
							{ table.getHeaderGroups().map( ( hg ) => (
								<tr key={ hg.id }>
									{ hg.headers.map( ( header ) => (
										<th
											key={ header.id }
											onClick={ header.column.getToggleSortingHandler() }
											className={ [
												`px-3 py-3 text-left font-semibold select-none border-b border-green-dark${
													fitWidth
														? ''
														: ' whitespace-nowrap'
												}`,
												header.column.getCanSort()
													? 'cursor-pointer hover:bg-yellow/30'
													: '',
												header.column.getIsSorted()
													? 'bg-yellow/30'
													: '',
											].join( ' ' ) }
										>
											{ flexRender(
												header.column.columnDef.header,
												header.getContext()
											) }
											{ header.column.getCanSort() && (
												<span className="ml-1 opacity-60 text-xs">
													{ { asc: '↑', desc: '↓' }[
														header.column.getIsSorted()
													] ?? '↕' }
												</span>
											) }
										</th>
									) ) }
								</tr>
							) ) }
						</thead>
						<tbody>
							{ table.getRowModel().rows.length === 0 ? (
								<tr>
									<td
										colSpan={ columns.length }
										className="px-3 py-8 text-center text-gray"
									>
										No se encontraron resultados.
									</td>
								</tr>
							) : (
								table.getRowModel().rows.map( ( row ) => {
									const isOwnItem =
										window.anacrm?.currentUserId &&
										row.original.author?.node
											?.databaseId ===
											Number(
												window.anacrm.currentUserId
											);
									return (
										<React.Fragment key={ row.id }>
											<tr
												className={ [
													'border-t border-green-dark hover:bg-yellow/30 transition-colors',
													isOwnItem
														? 'bg-green-dark/10 hover:'
														: '',
													getRowClassName?.(
														row.original
													) ?? '',
												].join( ' ' ) }
											>
												{ row
													.getVisibleCells()
													.map( ( cell ) => (
														<td
															key={ cell.id }
															className="px-3 py-2 align-top"
														>
															{ flexRender(
																cell.column
																	.columnDef
																	.cell,
																cell.getContext()
															) }
														</td>
													) ) }
											</tr>
											{ renderSubRow &&
												row.getIsExpanded() && (
													<tr className="border-t border-green-dark/30">
														<td
															colSpan={
																columns.length
															}
															className="p-0"
														>
															{ renderSubRow(
																row
															) }
														</td>
													</tr>
												) }
										</React.Fragment>
									);
								} )
							) }
						</tbody>
					</table>
				</div>
			</div>

			{ /* Pagination */ }
			{ total > pageSize && (
				<div className="flex items-center gap-3 text-sm">
					<button
						onClick={ () => table.previousPage() }
						disabled={ ! table.getCanPreviousPage() }
						className="px-3 py-1.5 border-2 border-black bg-white hover:bg-gray-light disabled:opacity-40 disabled:cursor-not-allowed"
					>
						← Anterior
					</button>

					<span className="text-gray">
						{ from }–{ to } de { total }
					</span>

					<button
						onClick={ () => table.nextPage() }
						disabled={ ! table.getCanNextPage() }
						className="px-3 py-1.5 border-2 border-black bg-white hover:bg-gray-light disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Siguiente →
					</button>
				</div>
			) }
		</div>
	);
}
